/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { TivoPayloadAction } from '../../core/tivo/tivo-redux-wrapper';
import {
    clearLastFocusedPickerStateAction,
    resetSeasonPickerStateAction,
    setLastFocusedPickerStateAction,
    setSeasonPickerHorizontalHighlightAction,
    setSeasonPickerInFocusAction,
} from './seasonpicker.actions';
import {
    CLEAR_LAST_FOCUSED_PICKER_STATE,
    RESET_SEASON_PICKER_STATE,
    SET_LAST_FOCUSED_PICKER_STATE,
    SET_LAST_FOCUSED_TYPE,
    SET_SEASON_PICKER_HIGHLIGHT,
    UPDATE_SEASON_PICKER_IN_FOCUS,
} from './seasonpicker.types';

export type LastFocusedPickerState = {
    seasonTitle: string;
    pickerIndex: number;
    headerPosition: number;
} | null;

export type LastFocusedTypeState = 'episode' | 'picker' | null;

type PickerItem = {
    index: number;
    isFocused: boolean;
};

type SeasonPickerState = {
    pickers: {
        [key: string]: PickerItem;
    };
    lastFocusedType?: LastFocusedTypeState;
    lastFocusedPickerState?: LastFocusedPickerState;
};

const initialState: SeasonPickerState = { pickers: {} };

const seasonPickerReducer = (
    state = initialState,
    action: TivoPayloadAction<any> = { type: '', payload: undefined },
) => {
    const { type, payload } = action;
    switch (type) {
        case SET_SEASON_PICKER_HIGHLIGHT:
            return {
                ...state,
                pickers: {
                    ...state.pickers,
                    [payload.pickerId]: { index: payload.index, isFocused: true },
                },
            };

        case UPDATE_SEASON_PICKER_IN_FOCUS:
            return {
                ...state,
                pickers: {
                    ...state.pickers,
                    [payload.pickerId]: {
                        ...state.pickers[payload.pickerId],
                        isFocused: payload.isFocused,
                    },
                },
            };

        case RESET_SEASON_PICKER_STATE:
            return {
                ...state,
                pickers: Object.keys(state.pickers).reduce(
                    (nextPickers, pickerId) => ({
                        ...nextPickers,
                        [pickerId]: {
                            ...state.pickers[pickerId],
                            isFocused: false,
                        },
                    }),
                    {} as { [key: string]: PickerItem },
                ),
            };

        case SET_LAST_FOCUSED_PICKER_STATE:
            return {
                ...state,
                lastFocusedPickerState: payload
                    ? {
                          seasonTitle: payload.seasonTitle,
                          pickerIndex: payload.pickerIndex,
                          headerPosition: payload.headerPosition,
                      }
                    : null,
            };

        case CLEAR_LAST_FOCUSED_PICKER_STATE:
            return {
                ...state,
                lastFocusedPickerState: null,
            };

        case SET_LAST_FOCUSED_TYPE:
            return {
                ...state,
                lastFocusedType: payload?.focusedType || null,
            };

        default:
            return state;
    }
};

export default seasonPickerReducer;

export const setSeasonPickerInFocus = (pickerId) => (dispatch: Function) => {
    dispatch(setSeasonPickerInFocusAction({ pickerId, isFocused: true }));
};

export const removeSeasonPickerFromFocus = (pickerId: string) => (dispatch: Function) => {
    dispatch(setSeasonPickerInFocusAction({ pickerId, isFocused: false }));
};

export const setSeasonPickerHighlight =
    (pickerId: string, index: number) => (dispatch: Function) => {
        dispatch(setSeasonPickerHorizontalHighlightAction({ pickerId, index }));
    };

export const onMouseOverSeasonPicker =
    (pickerId: string, itemIndex: number) => (dispatch: Function) => {
        dispatch(setSeasonPickerHighlight(pickerId, itemIndex));
    };

export const onMouseOutSeasonPicker = (pickerId: string) => (dispatch: Function) => {
    dispatch(removeSeasonPickerFromFocus(pickerId));
};

export const resetSeasonPickersFocus = () => (dispatch: Function) => {
    dispatch(resetSeasonPickerStateAction());
};

export const setLastFocusedType =
    (focusedType: 'episode' | 'picker' | '') => (dispatch: Function) => {
        dispatch({ type: SET_LAST_FOCUSED_TYPE, payload: { focusedType } });
    };

export const setLastFocusedPickerState =
    (seasonTitle: string, pickerIndex: number, headerPosition: number) => (dispatch: Function) => {
        dispatch(setLastFocusedPickerStateAction({ seasonTitle, pickerIndex, headerPosition }));
    };

export const clearLastFocusedPickerState = () => (dispatch: Function) => {
    dispatch(clearLastFocusedPickerStateAction());
};

export const selectIsAnyPickerGhostActive = (
    state: any,
    ghostMode: boolean,
    isMainListFocused: boolean,
): boolean => {
    const { pickers, lastFocusedType } = state.seasonPicker;
    const anyPickerFocused = Object.values(pickers || {}).some(
        (picker: any) => picker && picker.isFocused,
    );
    if (!ghostMode || anyPickerFocused || lastFocusedType !== 'picker' || isMainListFocused) {
        return false;
    }
    return Object.values(pickers || {}).some((picker: any) => picker && picker.index >= 0);
};
